import { type ImageKitParams } from '@/app/components/media/app-image/shared/imagekit-types';

export const getImageKitTransformKey = (transform: string): string => {
  if (transform.startsWith('e-')) {
    const nextSeparator = transform.indexOf('-', 2);
    return nextSeparator === -1 ? transform : transform.slice(0, nextSeparator);
  }

  const separator = transform.indexOf('-');
  return separator === -1 ? transform : transform.slice(0, separator);
};

/** Split ImageKit `tr` on commas, keeping commas inside `(...)` (e.g. rgba). */
export const splitImageKitTransformList = (value: string): string[] => {
  const segments: string[] = [];
  let current = '';
  let depth = 0;

  for (const char of value) {
    if (char === '(') depth += 1;
    if (char === ')') depth = Math.max(0, depth - 1);

    if (char === ',' && depth === 0) {
      if (current) segments.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  if (current) segments.push(current);

  // Reattach bare numeric pieces from values like `bg-255,0,0`.
  const transforms: string[] = [];
  for (const segment of segments) {
    if (/^\d+(\.\d+)?$/.test(segment) && transforms.length > 0) {
      transforms[transforms.length - 1] += `,${segment}`;
      continue;
    }
    transforms.push(segment);
  }
  return transforms;
};

export const parseImageKitTransforms = (value?: unknown): string[] => {
  const raw =
    typeof value === 'string'
      ? value
      : Array.isArray(value)
        ? value.join(',')
        : '';
  return raw ? splitImageKitTransformList(raw) : [];
};

export const joinImageKitTransforms = (
  transforms: string[]
): string | undefined => (transforms.length ? transforms.join(',') : undefined);

export const dedupeImageKitTransforms = (transforms: string[]): string[] => {
  const map = new Map<string, string>();
  for (const transform of transforms) {
    map.set(getImageKitTransformKey(transform), transform);
  }
  return Array.from(map.values());
};

export const imageKitParamsToTransforms = (
  params?: ImageKitParams
): string[] => {
  if (!params) return [];
  const out: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    if (k === 'tr') {
      splitImageKitTransformList(String(v)).forEach((seg) => out.push(seg));
      continue;
    }
    out.push(`${k}-${v}`);
  }
  return out;
};

export const toStableImageKitParamsKey = (
  params?: Record<string, unknown>
): string => {
  if (!params) return '';

  return Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${String(value)}`)
    .join(';');
};
